import { apiFetch, parseErrorDetail, ApiError } from "@/api/fetch";

export class PaperRepoRequestError extends ApiError {
  constructor(message: string, status: number) {
    super(message, status);
    this.name = "PaperRepoRequestError";
  }
}

export interface PaperRepoSummary {
  id: string;
  name: string;
  description: string | null;
  active_branch: string;
  paper_count: number;
  branch_count: number;
  created_at: string;
}

export interface PaperReposListEnvelope {
  repos: PaperRepoSummary[];
}

export interface PaperRepoBranchSummary {
  name: string;
  active: boolean;
  paper_count: number;
  commit_count: number;
}

export interface PaperRepoDetail {
  id: string;
  name: string;
  description: string | null;
  active_branch: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  branches: PaperRepoBranchSummary[];
}

/** Paper row returned from repo endpoints. */
export interface RepoPaperRow {
  id: string;
  paper_id: string | null;
  custom_paper_id: string | null;
  title: string;
  authors: string | null;
  year: number | null;
  journal: string | null;
  volume: string | null;
  issue: string | null;
  pages: string | null;
  doi: string | null;
  pmid: string | null;
  pmc_id: string | null;
  abstract: string | null;
  entry_type: string | null;
  source_key: string | null;
  import_source: string | null;
  cited_by_count: number | null;
  s2_id: string | null;
  pdf_filename: string | null;
  pdf_gcs_path: string | null;
  tags: string[];
  notes: string | null;
  created_at: string;
  updated_at: string;
  branch: string;
  repo_note?: string | null;
  annotations?: unknown[];
}

export interface RepoPaperListResponse {
  papers: RepoPaperRow[];
  total: number;
  page: number;
  per_page: number;
}

export interface RepoCommitSummary {
  sha: string;
  parent_sha: string | null;
  message: string | null;
  paper_count: number;
  committed_at: string;
}

export interface RepoCommitListResponse {
  commits: RepoCommitSummary[];
  total: number;
  page: number;
  per_page: number;
}

export const listPaperRepos = async (): Promise<PaperRepoSummary[]> => {
  const response = await apiFetch("/paper-repos/", { method: "GET" });
  if (!response.ok) {
    throw new PaperRepoRequestError(await parseErrorDetail(response), response.status);
  }
  const data = (await response.json()) as PaperReposListEnvelope;
  return data.repos ?? [];
};

export const getPaperRepo = async (
  repoId: string,
): Promise<PaperRepoDetail | null> => {
  const response = await apiFetch(
    `/paper-repos/${encodeURIComponent(repoId)}`,
    { method: "GET" },
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new PaperRepoRequestError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<PaperRepoDetail>;
};

export interface ListRepoPapersParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: string;
  sort_dir?: string;
  branch?: string;
}

export const listRepoPapers = async (
  repoId: string,
  params: ListRepoPapersParams = {},
): Promise<RepoPaperListResponse> => {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.per_page) qs.set("per_page", String(params.per_page));
  if (params.search) qs.set("search", params.search);
  if (params.sort_by) qs.set("sort_by", params.sort_by);
  if (params.sort_dir) qs.set("sort_dir", params.sort_dir);
  if (params.branch) qs.set("branch", params.branch);

  const url = `/paper-repos/${encodeURIComponent(repoId)}/papers${qs.toString() ? `?${qs}` : ""}`;
  const response = await apiFetch(url, { method: "GET" });
  if (!response.ok) {
    throw new PaperRepoRequestError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<RepoPaperListResponse>;
};

export interface ListRepoCommitsParams {
  page?: number;
  per_page?: number;
  branch?: string;
}

export const listRepoCommits = async (
  repoId: string,
  params: ListRepoCommitsParams = {},
): Promise<RepoCommitListResponse> => {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.per_page) qs.set("per_page", String(params.per_page));
  if (params.branch) qs.set("branch", params.branch);

  const url = `/paper-repos/${encodeURIComponent(repoId)}/commits${qs.toString() ? `?${qs}` : ""}`;
  const response = await apiFetch(url, { method: "GET" });
  if (!response.ok) {
    throw new PaperRepoRequestError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<RepoCommitListResponse>;
};

/** Export formats accepted by GET /paper-repos/{id}/export?format= */
export type PaperRepoExportFormat = "bibtex" | "bib" | "ris" | "markdown" | "md" | "csv";

export interface PaperRepoAddPaperItem {
  paper_id: string;
  title?: string | null;
  doi?: string | null;
}

export interface PaperRepoAddPapersPayload {
  items: PaperRepoAddPaperItem[];
  branch?: string | null;
}

export interface PaperRepoAddPapersResult {
  added: string[];
  skipped: string[];
  total: number;
}

export interface PaperRepoPaperPatchPayload {
  repo_note?: string | null;
  append_annotation?: { note: string; lines?: string | null } | null;
}

export interface PaperRepoCommitPayload {
  message: string;
  branch?: string | null;
}

export interface PaperRepoCommitResult {
  sha: string;
  parent_sha: string | null;
  message: string;
  paper_count: number;
  paper_ids: string[];
  added: string[];
  removed: string[];
  timestamp: string;
}

export interface PaperRepoCreateBranchPayload {
  name: string;
  from_branch?: string | null;
}

export interface PaperRepoCreateBranchResult {
  branch: string;
  papers: number;
}

export interface PaperRepoMergePayload {
  source: string;
  target?: string | null;
}

export interface PaperRepoMergeResult {
  merged: number;
  total: number;
  source: string;
  target: string;
  added: string[];
}

export interface PaperRepoStatus {
  name: string;
  description?: string;
  active_branch: string;
  branch_count: number;
  paper_count: number;
  commit_count: number;
  searches: unknown[];
  papers: Record<string, Record<string, unknown>>;
}

export interface PaperRepoDiff {
  added: string[];
  removed: string[];
  ref_a: string;
  ref_b: string;
  ref_a_info: Record<string, unknown>;
  ref_b_info: Record<string, unknown>;
}

/** POST /paper-repos/ */
export const createPaperRepo = async (
  body: { name: string; description?: string | null },
): Promise<PaperRepoSummary> => {
  const response = await apiFetch("/paper-repos/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new PaperRepoRequestError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<PaperRepoSummary>;
};

export interface PaperRepoPatchPayload {
  active_branch?: string;
  description?: string | null;
  is_public?: boolean;
}

/** PATCH /paper-repos/{id} — active branch and/or repo settings */
export const patchPaperRepo = async (
  repoId: string,
  body: PaperRepoPatchPayload,
): Promise<PaperRepoDetail> => {
  const response = await apiFetch(`/paper-repos/${encodeURIComponent(repoId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new PaperRepoRequestError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<PaperRepoDetail>;
};

export interface PaperRepoImportReferencesPayload {
  seed_paper_id: string;
  doi?: string | null;
  min_citations?: number;
  limit?: number | null;
  branch?: string | null;
}

export interface PaperRepoImportReferencesResult {
  added: string[];
  skipped: string[];
  total: number;
  references_fetched: number;
  matched_in_corpus: number;
}

/** POST /paper-repos/{id}/import-references — add cited papers from bibliography (Semantic Scholar). */
export const importReferencesToPaperRepo = async (
  repoId: string,
  payload: PaperRepoImportReferencesPayload,
): Promise<PaperRepoImportReferencesResult> => {
  const response = await apiFetch(
    `/paper-repos/${encodeURIComponent(repoId)}/import-references`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) {
    throw new PaperRepoRequestError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<PaperRepoImportReferencesResult>;
};

/** POST /paper-repos/{id}/papers */
export const addPapersToRepo = async (
  repoId: string,
  payload: PaperRepoAddPapersPayload,
): Promise<PaperRepoAddPapersResult> => {
  const response = await apiFetch(
    `/paper-repos/${encodeURIComponent(repoId)}/papers`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) {
    throw new PaperRepoRequestError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<PaperRepoAddPapersResult>;
};

/** DELETE /paper-repos/{id}/papers/{entry_id} */
export const deleteRepoPaperEntry = async (
  repoId: string,
  entryId: string,
  branch?: string | null,
): Promise<{ removed: string[]; total: number }> => {
  const qs = new URLSearchParams();
  if (branch) qs.set("branch", branch);
  const q = qs.toString();
  const url = `/paper-repos/${encodeURIComponent(repoId)}/papers/${encodeURIComponent(entryId)}${q ? `?${q}` : ""}`;
  const response = await apiFetch(url, { method: "DELETE" });
  if (!response.ok) {
    throw new PaperRepoRequestError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<{ removed: string[]; total: number }>;
};

/** PATCH /paper-repos/{id}/papers/{entry_id} */
export const patchRepoPaperEntry = async (
  repoId: string,
  entryId: string,
  payload: PaperRepoPaperPatchPayload,
  branch?: string | null,
): Promise<Record<string, unknown>> => {
  const qs = new URLSearchParams();
  if (branch) qs.set("branch", branch);
  const q = qs.toString();
  const url = `/paper-repos/${encodeURIComponent(repoId)}/papers/${encodeURIComponent(entryId)}${q ? `?${q}` : ""}`;
  const response = await apiFetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new PaperRepoRequestError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<Record<string, unknown>>;
};

/** POST /paper-repos/{id}/commits */
export const commitPaperRepo = async (
  repoId: string,
  payload: PaperRepoCommitPayload,
): Promise<PaperRepoCommitResult> => {
  const response = await apiFetch(
    `/paper-repos/${encodeURIComponent(repoId)}/commits`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) {
    throw new PaperRepoRequestError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<PaperRepoCommitResult>;
};

/** POST /paper-repos/{id}/branches */
export const createPaperRepoBranch = async (
  repoId: string,
  payload: PaperRepoCreateBranchPayload,
): Promise<PaperRepoCreateBranchResult> => {
  const response = await apiFetch(
    `/paper-repos/${encodeURIComponent(repoId)}/branches`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) {
    throw new PaperRepoRequestError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<PaperRepoCreateBranchResult>;
};

/** POST /paper-repos/{id}/merge */
export const mergePaperRepoBranches = async (
  repoId: string,
  payload: PaperRepoMergePayload,
): Promise<PaperRepoMergeResult> => {
  const response = await apiFetch(
    `/paper-repos/${encodeURIComponent(repoId)}/merge`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) {
    throw new PaperRepoRequestError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<PaperRepoMergeResult>;
};

/** GET /paper-repos/{id}/status */
export const getPaperRepoStatus = async (
  repoId: string,
  branch?: string | null,
): Promise<PaperRepoStatus> => {
  const qs = new URLSearchParams();
  if (branch) qs.set("branch", branch);
  const q = qs.toString();
  const url = `/paper-repos/${encodeURIComponent(repoId)}/status${q ? `?${q}` : ""}`;
  const response = await apiFetch(url, { method: "GET" });
  if (!response.ok) {
    throw new PaperRepoRequestError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<PaperRepoStatus>;
};

/** GET /paper-repos/{id}/diff */
export const getPaperRepoDiff = async (
  repoId: string,
  refA: string,
  refB: string,
): Promise<PaperRepoDiff> => {
  const qs = new URLSearchParams({ ref_a: refA, ref_b: refB });
  const url = `/paper-repos/${encodeURIComponent(repoId)}/diff?${qs}`;
  const response = await apiFetch(url, { method: "GET" });
  if (!response.ok) {
    throw new PaperRepoRequestError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<PaperRepoDiff>;
};

/**
 * Downloads an export blob (authenticated). Caller triggers save with the filename from Content-Disposition when present.
 */
export const downloadPaperRepoExport = async (
  repoId: string,
  format: PaperRepoExportFormat,
  branch?: string | null,
): Promise<Blob> => {
  const qs = new URLSearchParams({ format });
  if (branch) qs.set("branch", branch);
  const url = `/paper-repos/${encodeURIComponent(repoId)}/export?${qs}`;
  const response = await apiFetch(url, { method: "GET" });
  if (!response.ok) {
    throw new PaperRepoRequestError(await parseErrorDetail(response), response.status);
  }
  return response.blob();
};
